/**
 * ManagerSnapshotCard Component
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { TrendingUp, TrendingDown, Minus, Shield } from 'lucide-react';
import type { ManagerIntelligenceStats } from '@/hooks/useManagerIntelligenceStats';

interface ManagerSnapshotCardProps {
  profile: { id: string; display_name: string | null; email: string | null; };
  stats: ManagerIntelligenceStats | null | undefined;
  isSelfView: boolean;
}

export function ManagerSnapshotCard({ profile, stats, isSelfView }: ManagerSnapshotCardProps) {
  const displayName = profile.display_name || profile.email || 'Manager';
  const initials = displayName.slice(0, 2).toUpperCase();

  const getTrendIcon = () => {
    if (!stats?.trendDirection) return <Minus className="h-4 w-4 text-muted-foreground" />;
    if (stats.trendDirection === 'improving') return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (stats.trendDirection === 'drifting') return <TrendingDown className="h-4 w-4 text-amber-500" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarFallback className="text-lg">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-xl">{displayName}</CardTitle>
              <Badge variant="outline"><Shield className="h-3 w-3 mr-1" />Manager</Badge>
            </div>
            <div className="flex items-center gap-2 mt-2">
              {getTrendIcon()}
              <span className="text-sm text-muted-foreground">
                {stats?.trendDirection === 'improving' ? 'Improving' : stats?.trendDirection === 'drifting' ? 'Needs attention' : 'Stable'}
              </span>
            </div>
          </div>
          {!isSelfView && stats?.systemStewardshipIndex != null && (
            <div className="text-center">
              <div className="text-xs text-muted-foreground mb-1">Stewardship Index</div>
              <div className="text-3xl font-bold">{Math.round(stats.systemStewardshipIndex)}</div>
            </div>
          )}
        </div>
      </CardHeader>
      {stats?.sampleCount != null && stats.sampleCount > 0 && (
        <CardContent className="pt-0">
          <p className="text-xs text-muted-foreground">Based on {stats.sampleCount} intervention{stats.sampleCount !== 1 ? 's' : ''}</p>
        </CardContent>
      )}
    </Card>
  );
}
